/**
 * Utility functions for managing authentication state in cookies and local storage
 */
import { authCookies } from './cookieUtils';

/**
 * Sets the authentication state in cookies only
 */
export const setAuthState = (isLoggedIn: boolean): void => {
  // Set in cookies only
  authCookies.setAuthState(isLoggedIn);
  
  // Clean up localStorage if it exists
  if (localStorage.getItem('isLoggedIn')) {
    localStorage.removeItem('isLoggedIn');
  }
};

/**
 * Gets the current authentication state from cookies only
 */
export const getAuthState = (): boolean => {
  // Get from cookies only
  return authCookies.getAuthState();
};

/**
 * Clears all authentication data from cookies and localStorage
 */
export const clearAuthData = async (): Promise<void> => {
  // Clear auth state from cookies
  authCookies.clearAuthState();
  
  // Clean up localStorage auth state if it exists
  if (localStorage.getItem('isLoggedIn')) {
    localStorage.removeItem('isLoggedIn');
  }
  
  // Clear all auth-related cookies
  const cookiesToClear = [
    'sb-access-token',
    'sb-refresh-token',
    'sb-auth-token',
    'isLoggedIn'
  ];
  
  // Clear the Supabase URL specific token
  const supabaseURL = "srrsrfwognluuaxudjhi";
  cookiesToClear.push(`sb-${supabaseURL}-auth-token`);
  
  // Clear cookies
  cookiesToClear.forEach(cookieName => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
  
  // Clear role cookies
  document.cookie.split(';').forEach(cookie => {
    const [name] = cookie.trim().split('=');
    if (name && name.startsWith('role_')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
  
  // Clean up all auth-related localStorage (one-time cleanup)
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.startsWith('sb-') || 
      key.startsWith('user-role-') || 
      key === 'isLoggedIn'
    )) {
      keysToRemove.push(key);
    }
  }
  
  // Remove all auth-related keys from localStorage
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear any other auth cookies
  document.cookie = "Took=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  
  // Small delay to ensure all operations complete
  return new Promise(resolve => setTimeout(resolve, 100));
};

/**
 * Checks if auth token is present in cookies/localStorage and sets isLoggedIn accordingly
 */
export const syncAuthState = (): void => {
  // Check cookies only for Supabase tokens
  let hasToken = false;
  
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name] = cookie.trim().split('=');
    if (name && name.startsWith('sb-')) {
      hasToken = true;
      break;
    }
  }
  
  // Update isLoggedIn in cookies only
  setAuthState(hasToken);
};

export default {
  setAuthState,
  getAuthState,
  clearAuthData,
  syncAuthState
};
